import { Database } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import type { CountryStatus, RegisterSourceMode } from '@/services/api/registers'
import { cn } from '@/utils/cn'
import { CountryTag } from './CountryTag'

const COUNTRY_LABEL_KEY = {
  cz: 'extensions.osint.countries.cz',
  sk: 'extensions.osint.countries.sk',
} as const

function Numeral({ value }: { value: number }) {
  const { i18n } = useTranslation()
  return <span className="font-mono tabular-nums">{value.toLocaleString(i18n.language)}</span>
}

/** The human-readable "which version is loaded" line, built from the store meta. */
export function describeVersions(
  country: CountryStatus['country'],
  versions: Record<string, string | number>,
  t: (k: string, o?: Record<string, unknown>) => string
): string[] {
  const lines: string[] = []
  if (versions.parsed_at)
    lines.push(
      t('extensions.osint.versions.parsedAt', {
        at: String(versions.parsed_at).slice(0, 16).replace('T', ' '),
      })
    )
  if (country === 'cz') {
    if (versions.cz_last_batch_vr != null)
      lines.push(t('extensions.osint.versions.czBatchVr', { n: versions.cz_last_batch_vr }))
    if (versions.cz_last_batch_res != null)
      lines.push(t('extensions.osint.versions.czBatchRes', { n: versions.cz_last_batch_res }))
  } else if (versions.sk_applied_until) {
    lines.push(t('extensions.osint.versions.skAppliedUntil', { day: versions.sk_applied_until }))
  }
  return lines
}

export interface SnapshotStatusCardProps {
  status: CountryStatus
  /** Where the snapshot is read from — decides who owns the version markers. Default: local. */
  sourceMode?: RegisterSourceMode
  className?: string
}

/**
 * One country's register snapshot: what is loaded, and which version of the
 * source registers it was built from.
 *
 * Read-only. The snapshot is refreshed by a schedule its operator owns, so there
 * is nothing to trigger from here (ADR-0016).
 *
 * The version markers come from the staging store the pipeline writes, which only
 * exists where that pipeline runs. Reading a hosted snapshot therefore leaves them
 * empty by design — that is the service's version to report, not a missing store
 * this deployment should go and build.
 */
export function SnapshotStatusCard({
  status,
  sourceMode = 'local',
  className,
}: SnapshotStatusCardProps) {
  const { t } = useTranslation()
  const versionLines = describeVersions(status.country, status.versions, t)

  return (
    <Card className={cn('relative overflow-hidden p-4 md:p-5', className)}>
      <div
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-10 size-40 rounded-full bg-primary/5 blur-2xl"
      />
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <CountryTag country={status.country === 'cz' ? 'CZ' : 'SK'} />
          <h3 className="text-sm font-semibold text-foreground">
            {t(COUNTRY_LABEL_KEY[status.country])}
          </h3>
        </div>
        {status.available ? (
          <Badge variant="success">{t('extensions.osint.snapshot.loaded')}</Badge>
        ) : (
          <Badge variant="warning">{t('extensions.osint.snapshot.notLoaded')}</Badge>
        )}
      </header>

      <div className="mt-4 grid grid-cols-3 gap-2 md:gap-3">
        {(
          [
            ['documents', status.documents],
            ['companies', status.companies],
            ['persons', status.persons],
          ] as const
        ).map(([key, value]) => (
          <div
            key={key}
            className="rounded-md border border-border/60 bg-muted/40 px-2 py-2 md:px-3"
          >
            <div className="text-2xs font-medium uppercase tracking-normal text-muted-foreground max-md:truncate md:tracking-[0.12em]">
              {t(`extensions.osint.snapshot.${key}`)}
            </div>
            <div className="mt-0.5 text-sm font-semibold leading-tight text-foreground md:text-lg">
              <Numeral value={value} />
            </div>
          </div>
        ))}
      </div>

      <dl className="mt-4 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-5 gap-y-1.5 text-xs">
        <dt className="text-muted-foreground">{t('extensions.osint.snapshot.collection')}</dt>
        <dd className="flex items-center gap-1.5 font-mono text-foreground max-md:break-all">
          <Database className="size-3.5 text-muted-foreground max-md:shrink-0" />
          {status.collection}
        </dd>
        <dt className="text-muted-foreground">{t('extensions.osint.snapshot.version')}</dt>
        <dd className="text-foreground">
          {versionLines.length ? (
            <ul className="space-y-0.5">
              {versionLines.map(line => (
                <li key={line} className="font-mono text-2xs">
                  {line}
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-muted-foreground">
              {t(
                sourceMode === 'hosted'
                  ? 'extensions.osint.snapshot.versionAtOperator'
                  : 'extensions.osint.snapshot.noStore'
              )}
            </span>
          )}
        </dd>
      </dl>
    </Card>
  )
}
